// Lines which can be selected as the target for a release profile:
import type { ReleaseProfileAttestationState } from '../../types/db.ts';

export type FormRLeafLine =
  | '5.1'
  | '5.2'
  | '5.3'
  | '5.4.1'
  | '5.4.2'
  | '5.5.1A'
  | '5.5.1B'
  | '5.5.2'
  | '5.5.3A'
  | '5.5.3B'
  | '5.5.4'
  | '6.1'
  | '6.2'
  | '8.2'
  | '8.4'
  | '8.6';

export const formRLeafLines: FormRLeafLine[] = [
  '5.1',
  '5.2',
  '5.3',
  '5.4.1',
  '5.4.2',
  '5.5.1A',
  '5.5.1B',
  '5.5.2',
  '5.5.3A',
  '5.5.3B',
  '5.5.4',
  '6.1',
  '6.2',
  '8.2',
  '8.4',
  '8.6',
];

// Lines/sections that are shown on the release profile assignment page:
export type FormRAssignmentLine = '5.1' | '5.2' | '5.3' | '5.4' | '5.5' | '6.1' | '6.2' | '8.2' | '8.4' | '8.6';

export const formRAssignmentLines: FormRAssignmentLine[] = [
  '5.1',
  '5.2',
  '5.3',
  '5.4',
  '5.5',
  '6.1',
  '6.2',
  '8.2',
  '8.4',
  '8.6',
];

export function formRLeafLineToAssignmentLine(line: FormRLeafLine | FormRAssignmentLine): FormRAssignmentLine {
  return line.slice(0, 3) as FormRAssignmentLine;
}

// Other line/section numbers which are used purely for grouping below in FORM_R_SECTIONS:
type FormRParentSection = '5' | '5.4' | '5.5' | '6' | '8';

type Section =
  | {
      id: FormRParentSection;
      title: string;
      subsections: Record<string, Section>;
    }
  | {
      id: FormRLeafLine;
      title: string;
    };

export const FORM_R_SECTIONS = {
  SECTION_5: {
    id: '5',
    title: 'Releases to the Environment On‑Site',
    subsections: {
      AIR_FUGITIVE: {
        id: '5.1',
        title: 'Fugitive or Non‑Point Air Emissions',
      },
      AIR_STACK: {
        id: '5.2',
        title: 'Stack or Point Air Emissions',
      },
      WATER: {
        id: '5.3',
        title: 'Discharges to Receiving Streams or Water Bodies',
      },
      INJECTION: {
        id: '5.4',
        title: 'Releases via Injection Wells',
        subsections: {
          CLASS_1: { id: '5.4.1', title: 'Class I Underground Injection Wells' },
          CLASS_OTHER: { id: '5.4.2', title: 'Class II–V Wells' },
        },
      },
      LAND: {
        id: '5.5',
        title: 'Releases to Land',
        subsections: {
          RCRA_C_LANDFILL: { id: '5.5.1A', title: 'RCRA Subtitle C Landfills' },
          OTHER_LANDFILL: { id: '5.5.1B', title: 'Other Landfills' },
          LAND_TREATMENT: { id: '5.5.2', title: 'Land Treatment/Application' },
          RCRA_C_IMPOUNDMENT: { id: '5.5.3A', title: 'RCRA Subtitle C Surface Impoundments' },
          OTHER_IMPOUNDMENT: { id: '5.5.3B', title: 'Other Surface Impoundments' },
          OTHER_DISPOSAL: { id: '5.5.4', title: 'Other Disposal to Land' },
        },
      },
    },
  },
  SECTION_6: {
    id: '6',
    title: 'Transfer(s) of the Toxic Chemical in Wastes to Off‑Site Locations',
    subsections: {
      POTW: {
        id: '6.1',
        title: 'Discharges to Publicly Owned Treatment Works (POTWs)',
      },
      OTHER: {
        id: '6.2',
        title: 'Transfers to Other Off‑Site Locations',
      },
    },
  },
  SECTION_8: {
    id: '8',
    title: 'SOURCE REDUCTION AND WASTE MANAGEMENT',
    subsections: {
      ENERGY_RECOVERY_ON_SITE: {
        id: '8.2',
        title: 'Quantity used for energy recovery on-site',
      },
      RECYCLED_ON_SITE: {
        id: '8.4',
        title: 'Quantity recycled on-site',
      },
      TREATED_ON_SITE: {
        id: '8.6',
        title: 'Quantity treated on-site',
      },
    },
  },
} satisfies Record<string, Section>;

export const FORM_R_SUBSECTIONS: { id: FormRLeafLine; title: string }[] = [];

function pushSectionsRecursively(arr: Section[], sections: Record<string, Section>) {
  for (const section of Object.values(sections)) {
    if ('subsections' in section) {
      pushSectionsRecursively(arr, section.subsections);
    } else {
      FORM_R_SUBSECTIONS.push(section);
    }
  }
}
pushSectionsRecursively(FORM_R_SUBSECTIONS, FORM_R_SECTIONS);

export const formRAssignmentLineNames: Record<FormRAssignmentLine, string> = {
  '5.1': FORM_R_SECTIONS.SECTION_5.subsections.AIR_FUGITIVE.title,
  '5.2': FORM_R_SECTIONS.SECTION_5.subsections.AIR_STACK.title,
  '5.3': FORM_R_SECTIONS.SECTION_5.subsections.WATER.title,
  '5.4': FORM_R_SECTIONS.SECTION_5.subsections.INJECTION.title,
  '5.5': FORM_R_SECTIONS.SECTION_5.subsections.LAND.title,
  '6.1': FORM_R_SECTIONS.SECTION_6.subsections.POTW.title,
  '6.2': FORM_R_SECTIONS.SECTION_6.subsections.OTHER.title,
  '8.2': FORM_R_SECTIONS.SECTION_8.subsections.ENERGY_RECOVERY_ON_SITE.title,
  '8.4': FORM_R_SECTIONS.SECTION_8.subsections.RECYCLED_ON_SITE.title,
  '8.6': FORM_R_SECTIONS.SECTION_8.subsections.TREATED_ON_SITE.title,
};

export const formRLeafLineNames: Record<FormRLeafLine, string> = {
  '5.1': FORM_R_SECTIONS.SECTION_5.subsections.AIR_FUGITIVE.title,
  '5.2': FORM_R_SECTIONS.SECTION_5.subsections.AIR_STACK.title,
  '5.3': FORM_R_SECTIONS.SECTION_5.subsections.WATER.title,
  '5.4.1': FORM_R_SECTIONS.SECTION_5.subsections.INJECTION.subsections.CLASS_1.title,
  '5.4.2': FORM_R_SECTIONS.SECTION_5.subsections.INJECTION.subsections.CLASS_OTHER.title,
  '5.5.1A': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.RCRA_C_LANDFILL.title,
  '5.5.1B': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.OTHER_LANDFILL.title,
  '5.5.2': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.LAND_TREATMENT.title,
  '5.5.3A': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.RCRA_C_IMPOUNDMENT.title,
  '5.5.3B': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.OTHER_IMPOUNDMENT.title,
  '5.5.4': FORM_R_SECTIONS.SECTION_5.subsections.LAND.subsections.OTHER_DISPOSAL.title,
  '6.1': FORM_R_SECTIONS.SECTION_6.subsections.POTW.title,
  '6.2': FORM_R_SECTIONS.SECTION_6.subsections.OTHER.title,
  '8.2': FORM_R_SECTIONS.SECTION_8.subsections.ENERGY_RECOVERY_ON_SITE.title,
  '8.4': FORM_R_SECTIONS.SECTION_8.subsections.RECYCLED_ON_SITE.title,
  '8.6': FORM_R_SECTIONS.SECTION_8.subsections.TREATED_ON_SITE.title,
};

export function formRLineAttestationsCreateBlank(): Record<FormRLeafLine, ReleaseProfileAttestationState> {
  return {
    '5.1': {},
    '5.2': {},
    '5.3': {},
    '5.4.1': {},
    '5.4.2': {},
    '5.5.1A': {},
    '5.5.1B': {},
    '5.5.2': {},
    '5.5.3A': {},
    '5.5.3B': {},
    '5.5.4': {},
    '6.1': {},
    '6.2': {},
    '8.2': {},
    '8.4': {},
    '8.6': {},
  };
}
